import {Component, ElementRef, EventEmitter, Input, OnChanges, Output, SecurityContext} from '@angular/core';
import {Rule} from '../../services/capd.model';
import {RulesService} from '../../services/rules.service';
import {DomSanitizer} from '@angular/platform-browser';
import {OrgService} from '../../services/org.service';
import {NavigationStart, Router} from '@angular/router';
import {AlertService} from '../../services/alert.service';
import {Marked} from '@ts-stack/markdown';

@Component({
    selector: 'library-rule-detail',
    templateUrl: './library-rule-detail.component.html',
    styleUrls: ['./library-rule-detail.component.less'],
})

export class LibraryRuleDetailComponent implements OnChanges {

    @Input() ruleDetail: Rule;
    @Input() newChange;
    @Output() listChange: EventEmitter<any> = new EventEmitter();
    @Output() deletedRule: EventEmitter<any> = new EventEmitter();

    ruleClone: Rule;
    rules: Rule[];
    categories: string[];
    showPreview = false;
    ruleChanged = false;
    loading: boolean;

    ruleDeleted = new EventEmitter();
    isDisabled = true;

    triggerEdited = new EventEmitter();
    newTrigger = '';
    oldId = '';
    triggerValid = false;

    constructor(
        private _service: RulesService,
        private _el: ElementRef,
        private _sanitize: DomSanitizer,
        private _router: Router,
        private alertService: AlertService
    ) { }

    ngOnInit() {
        this._router.events.subscribe((event: any) => {
            if (this.ruleChanged && event.url !== this._router.routerState.snapshot.url && event instanceof NavigationStart) {
                if (confirm('You are about to leave the page without saving; all unsaved changes will be lost. Would you like to proceed and abandon current changes?')){
                    // continue nav by setting ruleChanged to false
                    this.ruleChanged = false;
                }
                else{
                    // do not leave current page
                    this._router.navigateByUrl(this._router.routerState.snapshot.url);
                }
            }

        });
    }

    /**
     * Called whenever a new rule is selected.  Does the following:
     * - Clones the rule to preserve the original version.
     * - Makes sure that a rule has an action, and creates empty actions for rules that do not.
     * - Sets the ruleChanged boolean to false.
     * - Returns packages containing selected rule.
     *
     * @func ngOnChanges
     */
    ngOnChanges() {
        this.loading = true;
        this.ruleClone = JSON.parse(JSON.stringify(this.ruleDetail));

        if (!this.ruleClone.renderEnvironment) {
            this.ruleClone.renderEnvironment = {FD: true, NR: false, HCC: false};
        }
        if (!this.ruleClone.triggerPriority) {
            this.ruleClone.triggerPriority = 'Normal';
        }

        this.ruleChanged = false;
    }



    /**
     * Toggle checkbox that disables the delete button by default.
     *
     * @func toggleDisabled
     */
    toggleDisabled() {
        this.isDisabled = !this.isDisabled;
    }

    /**
     * Passes changes to the list of rules whenever a rule is modified or deleted.
     *
     * @func passChanges
     * @param {any} change - either an ID string (of new rule to select) or a null object.
     */
    passChanges(change) {
        this.listChange.emit(change);
    }

    /**
     * Toggle to show the preview card.
     *
     * @func togglePreview
     */
    togglePreview() {
        this.showPreview = !this.showPreview;
    }

    /**
     * Toggle to show the trigger editing modal.
     *
     * @func showTriggerEdit
     */
    showTriggerEdit() {
        $(this._el.nativeElement).find('#modal-edit-trigger').show();
    }

    hideTriggerEdit() {
        $(this._el.nativeElement).find('#modal-edit-trigger').hide();
    }

    /**
     * Changes the render environment of the given rule.
     *
     * Depending on the render environment, the following needs to be done:
     * - check if the environment is already set properly, and if so make no changes
     * - otherwise, set the render environment
     * - if the environment uses identical syntax to another environment (i.e. FD and HCC), you'll need to check
     *   if the previous environment used a different syntax.  If so, you'll need to reset the rule's action to a default state.
     * - If the environment has a unique syntax, you'll need to reset the rule's action as well, but won't need to bother checking.
     *
     * @func changeEnv
     * @param {string} env - the new environment to change to, given to the switch statement.
     */
    changeEnv(env: string) {
        this.ruleClone.renderEnvironment[env] = !this.ruleClone.renderEnvironment[env];
    }

    updateDocLevelCheck() {
        this.ruleClone.docLevel = !this.ruleClone.docLevel;
    }

    /**
     * Saves the rule data and returns it to the list to be updated.
     *
     * @async
     * @func onSave
     */
    onSave() {
        this._service.updateRuleV2(this.ruleClone).then(() => {
            this.passChanges(this.ruleClone);
        }).then(() => {
            this.ruleChanged = false;
            this.alertService.showAlert('Saved Successfully');
        });
    }

    /**
     * Checks to see if the rule is different from how it was before.
     *
     * @func isEdited
     */
    isEdited() {
        this.ruleChanged = !(JSON.stringify(this.ruleClone) === JSON.stringify(this.ruleDetail));
    }

    /**
     * Submits the changes to the server.
     *
     * @async
     * @func onSubmit
     */
    onSubmit() {
        this.ruleClone.triggerCode = this.newTrigger;
        this.oldId = this.ruleClone._id;
        this.ruleClone._id = '';

        this._service.deleteRule(this.oldId).then(() => {
            this._service.addRule(this.ruleClone).then(newTrigger => {
                this.triggerEdited.emit(newTrigger);
            });
        });
    }

    /**
     * Checks to see if the new trigger is nonzero and different from the previous version.
     *
     * @func isValid
     */
    isValid() {
        this.triggerValid = !(this.ruleClone.triggerCode === this.newTrigger) && this.newTrigger.length > 0;
    }


    /**
     * Parses the title for the card, using Angular's safe sanitization.
     *
     * @func titlePase
     * @param {string} toParse - the raw text to parse into a displayable title.
     * @returns {string} the safely sanitized title.
     */
    titleParse(toParse: string) {
        if (!!toParse) {
            return this._sanitize.sanitize(SecurityContext.HTML, this.ensureSafeBlankTargetLinks(toParse));
        }
    }

    /**
     * Parses the message of the card, both using HTML and Markdown with safe sanitization.
     *
     * @func messageParse
     * @param {string} toParse - the raw text to parse into a displayable message.
     * @returns {string} the safely sanitized message.
     */
    messageParse(toParse: string) {
        if (!!toParse) {
            const markdownHtml = Marked.parse(toParse);
            return this._sanitize.sanitize(SecurityContext.HTML, this.ensureSafeBlankTargetLinks(markdownHtml));
        }
    }

    private ensureSafeBlankTargetLinks(html: string): string {
        return html.replace(/<a\b([^>]*\btarget\s*=\s*(['"])_blank\2[^>]*)>/gi, (fullMatch, attrs) => {
            const relMatch = attrs.match(/\brel\s*=\s*(['"])(.*?)\1/i);
            if (!relMatch) {
                return `<a${attrs} rel="noopener noreferrer">`;
            }

            const quote = relMatch[1];
            const currentRel = relMatch[2];
            const relTokens = currentRel.split(/\s+/).filter(Boolean);

            if (!relTokens.includes('noopener')) {
                relTokens.push('noopener');
            }
            if (!relTokens.includes('noreferrer')) {
                relTokens.push('noreferrer');
            }

            const updatedRel = `rel=${quote}${relTokens.join(' ')}${quote}`;
            return fullMatch.replace(relMatch[0], updatedRel);
        });
    }

    closePreviewModal() {
        this.showPreview = false;
    }
}